import { AsyncPipe, NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { InlineSVGModule } from '@digilize/shared/ui/inline-svg/src';
import { TranslocoModule } from '@jsverse/transloco';
import { catchError, first, Observable, of, Subject, take, takeUntil } from 'rxjs';

import { CartState } from '../../application/states/cart.state';
import { CheckoutState } from '../../application/states/checkout.state';
import { ShowState } from '../../application/states/show.state';
import {
  hasOptions,
  type Product,
  type Supplement,
  type TicketExtraStep,
  ticketExtraSteps,
} from '../../models/product';
import { ConfiguredItemsComponent, type ConfiguredItemsTicket } from '../configured-items/configured-items.component';
import { OptionsQuantityComponent } from '../configured-items/options-quantity.component';
import { LocaleCurrencyPipe } from '../locale-currency.pipe';
import { ResizePipe } from '../media/resize.pipe';
import { ProductOptionsService } from '../product-options-dialog/product-options.service';
import { QuantityControlComponent } from '../quantity-control/quantity-control.component';

/**
 * The extras offered under a ticket product, one group per step, each reserved under one of its tickets,
 * or under the one ticket `ticketLineId` names. A step's extras are each other's siblings, so "only one of
 * these" holds within a step and not across steps (PAUL-3789).
 */
@Component({
  selector: 'plys-ticket-extras',
  templateUrl: './ticket-extras.component.html',
  styleUrls: ['./ticket-extras.component.scss', '../../styles/list-item.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    NgClass,
    NgOptimizedImage,
    NgTemplateOutlet,
    TranslocoModule,
    InlineSVGModule,
    LocaleCurrencyPipe,
    ResizePipe,
    QuantityControlComponent,
    OptionsQuantityComponent,
    ConfiguredItemsComponent,
  ],
})
export class TicketExtrasComponent implements OnDestroy {
  /** The ticket product the extras hang under. */
  @Input({ required: true }) product: Product;
  @Input({ required: true }) set supplements(supplements: Supplement[]) {
    this.steps = ticketExtraSteps(supplements ?? []);
  }
  /** How to add another ticket of the product; null where a ticket cannot be added from here. */
  @Input() ticket: ConfiguredItemsTicket | null = null;
  /** The one ticket the extras go under (seats-first, PAUL-3787); null spreads them over the product's tickets. */
  @Input() ticketLineId: string | null = null;

  protected steps: TicketExtraStep[] = [];
  protected readonly hasOptions = hasOptions;
  protected readonly reserving$ = this.cartState.reserving$;
  protected readonly menuUrl$ = this.showState.showMenuRedirectUrl$;
  private readonly expandDetails: Record<string, boolean> = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cartState: CartState,
    private readonly checkoutState: CheckoutState,
    private readonly showState: ShowState,
    private readonly productOptions: ProductOptionsService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** A deal or a variant product: choose first, then reserve under the ticket. */
  openOptions(supplement: Supplement, siblings: Supplement[]): void {
    this.productOptions
      .addItem(supplement, siblings, this.product.id, undefined, this.ticketLineId)
      .pipe(
        take(1), // A closed dialog emits nothing: end quietly rather than as an error.
        takeUntil(this.destroy$),
        catchError((err: Error) => {
          if (!(err instanceof HttpErrorResponse)) this.checkoutState.setError(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  add(supplement: Supplement, siblings: Supplement[]): void {
    this.cartState
      .addItem(supplement, siblings, this.product.id, {}, this.ticketLineId)
      .pipe(
        first(),
        takeUntil(this.destroy$),
        catchError((err: Error) => {
          this.checkoutState.setError(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  remove(supplement: Supplement): void {
    this.cartState
      .removeItem(supplement.id, null, this.ticketLineId)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe();
  }

  canAddMore(supplement: Supplement): Observable<boolean> {
    return this.cartState.canAddMore(supplement, this.ticketLineId);
  }

  maxQuantity(supplement: Supplement): Observable<number> {
    return this.cartState.getItemMaxQuantity(supplement, this.ticketLineId);
  }

  quantity(supplement: Supplement): Observable<number> {
    return this.ticketLineId
      ? this.cartState.getQuantityUnder(supplement.id, this.ticketLineId)
      : this.cartState.getSupplementQuantity(supplement.id, this.product.id);
  }

  isMenuVisible(supplement: Supplement): boolean {
    return supplement.hasReservation;
  }

  toggleDetails(supplementId: string): void {
    this.expandDetails[supplementId] = !this.expandDetails[supplementId];
  }

  isExpanded(supplementId: string): boolean {
    return this.expandDetails[supplementId] || false;
  }
}
